class NorthrynHeaderSearch extends HTMLElement {
  connectedCallback() {
    if (this._searchAbort) return;
    this.toggleButton = this.querySelector('[data-search-toggle]');
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('.nr-search-suggestions');
    this.header = this.closest('header');
    this.iconGroup = this.closest('.header__icons');
    if (!this.toggleButton || !this.form || !this.input || !this.results || !this.header) return;

    this.syncPallyTheme();
    this._queryVersion = 0;
    this._searchAbort = new AbortController();
    const options = { signal: this._searchAbort.signal };
    this.toggleButton.addEventListener('click', (event) => {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (this.hasAttribute('data-open')) this.closeSearch(true);
      else this.openSearch();
    }, options);
    this.input.addEventListener('input', (event) => {
      if (!event.isComposing) this.queueSuggestions();
    }, options);
    this.input.addEventListener('compositionend', () => this.queueSuggestions(), options);
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' && !this.results.hidden) {
        const first = this.results.querySelector('a');
        if (first) {
          event.preventDefault();
          first.focus();
        }
      }
    }, options);
    this.results.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const links = Array.from(this.results.querySelectorAll('a')).filter((link) => link.getClientRects().length);
      const index = links.indexOf(document.activeElement);
      if (index < 0) return;
      event.preventDefault();
      if (event.key === 'ArrowUp' && index === 0) this.input.focus();
      else links[Math.max(0, Math.min(links.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))].focus();
    }, options);
    this.form.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) event.preventDefault();
    }, options);
    document.addEventListener('pointerdown', (event) => {
      if (this.hasAttribute('data-open') && !this.contains(event.target)) this.closeSearch();
    }, options);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('data-open')) {
        event.preventDefault();
        event.stopPropagation();
        this.closeSearch(true);
      }
    }, options);
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closeSearch();
    }, options);
    window.addEventListener('resize', () => {
      if (!this.hasAttribute('data-open')) return;
      if (!this.getClientRects().length) this.closeSearch();
      else this.updateSearchLayout();
    }, options);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        if (this.hasAttribute('data-open')) this.updateSearchLayout();
      }, options);
    }
  }

  disconnectedCallback() {
    if (this._searchAbort) this._searchAbort.abort();
    this._searchAbort = null;
    clearTimeout(this._closeTimer);
    this.cancelSuggestions();
    this.removeAttribute('data-open');
    if (this.form) {
      this.form.hidden = true;
      this.form.inert = true;
    }
    if (this.results) this.results.hidden = true;
    if (this.iconGroup) this.iconGroup.classList.remove('nr-search-layer');
    if (this.toggleButton) this.toggleButton.setAttribute('aria-expanded', 'false');
  }

  syncPallyTheme() {
    const panel = document.querySelector('#SearchDrawer .drawer__inner');
    const input = document.querySelector('#SearchDrawer input[type="search"], #SearchDrawer .form-control');
    const button = document.querySelector('#SearchDrawer .btn--primary, #MenuDrawer .account-button, .btn--primary');
    const card = document.querySelector('.product-card, .card-product, .dropdown__container');
    const apply = (key, value) => { if (value) this.style.setProperty(key, value); };
    apply('--pally-search-text', getComputedStyle(this.header).color);
    if (panel) apply('--pally-search-panel-radius', getComputedStyle(panel).borderRadius);
    else if (card) apply('--pally-search-panel-radius', getComputedStyle(card).borderRadius);
    if (input) apply('--pally-search-input-radius', getComputedStyle(input).borderRadius);
    if (button) {
      const style = getComputedStyle(button);
      apply('--pally-search-button-radius', style.borderRadius);
      apply('--pally-search-button-text', style.color);
      if (style.backgroundColor !== 'rgba(0, 0, 0, 0)') {
        apply('--pally-search-button-bg', style.backgroundColor);
        apply('--pally-search-accent', style.backgroundColor);
      }
    }
  }

  updateSearchLayout() {
    const headerRect = this.header.getBoundingClientRect();
    const triggerRect = this.toggleButton.getBoundingClientRect();
    const hostRect = this.getBoundingClientRect();
    const style = getComputedStyle(this.header);
    const isLeft = this.dataset.side === 'left';
    const edgeLeft = headerRect.left + (parseFloat(style.paddingLeft) || 0);
    const edgeRight = headerRect.right - (parseFloat(style.paddingRight) || 0);
    let left = edgeLeft;
    let right = edgeRight;

    // Keep the field on the icon row; covering navigation text is intentional.
    this.header.querySelectorAll('.menu-drawer-button, .header__account-link, .cart-drawer-button').forEach((element) => {
      if (!element.getClientRects().length || getComputedStyle(element).visibility === 'hidden') return;
      const rect = element.getBoundingClientRect();
      if (rect.bottom <= triggerRect.top || rect.top >= triggerRect.bottom) return;
      if (!isLeft && rect.right <= triggerRect.left) left = Math.max(left, rect.right + 12);
      if (isLeft && rect.left >= triggerRect.right) right = Math.min(right, rect.left - 12);
    });

    const available = isLeft ? right - triggerRect.left : triggerRect.right - left;
    const width = Math.floor(Math.min(390, Math.max(0, available)));
    this.removeAttribute('data-below');
    this.style.setProperty('--nr-search-width', `${width}px`);
    const fieldHeight = window.matchMedia('(max-width: 749px)').matches ? 42 : 46;
    const resultsTop = hostRect.height / 2 + fieldHeight / 2 + 8;
    this.style.setProperty('--nr-search-results-top', `${resultsTop}px`);
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const panelWidth = Math.min(mobile ? 330 : 644, window.innerWidth - (mobile ? 20 : 32));
    const preferredLeft = isLeft ? triggerRect.left : triggerRect.right - panelWidth;
    const panelLeft = Math.max(10, Math.min(preferredLeft, window.innerWidth - panelWidth - 10));
    this.style.setProperty('--nr-results-left', `${panelLeft - hostRect.left}px`);
    this.style.setProperty('--nr-results-right', `${hostRect.right - panelLeft - panelWidth}px`);
    const viewportBottom = window.visualViewport
      ? window.visualViewport.offsetTop + window.visualViewport.height
      : window.innerHeight;
    this.style.setProperty('--nr-results-max-height', `${Math.max(80, viewportBottom - hostRect.top - resultsTop - 12)}px`);
  }

  openSearch() {
    document.querySelectorAll('nr-header-search[data-open]').forEach((search) => {
      if (search !== this) search.closeSearch(false, true);
    });
    clearTimeout(this._closeTimer);
    if (this.iconGroup) this.iconGroup.classList.add('nr-search-layer');
    this.syncPallyTheme();
    this.updateSearchLayout();
    this.form.hidden = false;
    this.form.inert = false;
    this.form.getBoundingClientRect();
    this.setAttribute('data-open', '');
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.input.focus({ preventScroll: true });
    if (this.input.value.trim().length >= 2) this.queueSuggestions();
  }

  closeSearch(restoreFocus = false, immediate = false) {
    if (!this.hasAttribute('data-open')) return;
    this.removeAttribute('data-open');
    this.toggleButton.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.toggleButton.focus({ preventScroll: true });
    this.form.inert = true;
    this.cancelSuggestions();
    this.results.hidden = true;
    clearTimeout(this._closeTimer);
    const finishClose = () => {
      if (this.hasAttribute('data-open')) return;
      this.form.hidden = true;
      if (this.iconGroup) this.iconGroup.classList.remove('nr-search-layer');
    };
    if (immediate) finishClose();
    else this._closeTimer = setTimeout(finishClose,
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 230);
  }

  cancelSuggestions() {
    clearTimeout(this._suggestTimer);
    if (this._suggestController) this._suggestController.abort();
    this._suggestController = null;
    this._queryVersion = (this._queryVersion || 0) + 1;
    if (this.results) this.results.removeAttribute('aria-busy');
  }

  queueSuggestions() {
    this.cancelSuggestions();
    this.results.hidden = true;
    const term = this.input.value.trim();
    if (term.length < 2 || !this.hasAttribute('data-open')) return;
    const version = this._queryVersion;
    this._suggestTimer = setTimeout(() => this.fetchSuggestions(term, version), 220);
  }

  isCurrentQuery(term, version) {
    return this.hasAttribute('data-open') && version === this._queryVersion && term === this.input.value.trim();
  }

  async fetchSuggestions(term, version) {
    if (!this.isCurrentQuery(term, version)) return;
    const controller = new AbortController();
    this._suggestController = controller;
    this.showSuggestionStatus('Searching…');
    this.results.setAttribute('aria-busy', 'true');
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || this.dataset.searchRoot || '/';
      const url = new URL(`${root.replace(/\/?$/, '/')}search/suggest`, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '8');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', 'header');
      const response = await fetch(url.href, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Predictive search unavailable');
      const html = await response.text();
      const documentHTML = new DOMParser().parseFromString(html, 'text/html');
      const payload = documentHTML.querySelector('#NrPredictiveSearchPayload');
      if (!payload) throw new Error('Predictive search payload missing');
      if (!this.isCurrentQuery(term, version)) return;
      this.results.replaceChildren(...Array.from(payload.childNodes).map((node) => node.cloneNode(true)));
      this.results.hidden = false;
      if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
        try { window.jdgm.customizeBadges(); } catch (_) {}
      }
    } catch (error) {
      if (error.name === 'AbortError' || !this.isCurrentQuery(term, version)) return;
      this.showSuggestionStatus('Suggestions are unavailable. Use Search to see results.');
      this.appendViewAll(term);
    } finally {
      if (this.isCurrentQuery(term, version)) this.results.removeAttribute('aria-busy');
      if (this._suggestController === controller) this._suggestController = null;
    }
  }

  showSuggestionStatus(message) {
    const status = document.createElement('p');
    status.className = 'nr-search-suggestions__status';
    status.setAttribute('role', 'status');
    status.textContent = message;
    this.results.replaceChildren(status);
    this.results.hidden = false;
  }

  appendViewAll(term) {
    const link = document.createElement('a');
    const url = new URL(this.form.action, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    link.href = url.href;
    link.className = 'nr-search-view-all';
    link.textContent = 'View all results';
    this.results.appendChild(link);
  }
}

if (!customElements.get('nr-header-search')) {
  customElements.define('nr-header-search', NorthrynHeaderSearch);
}

class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  init() {
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      // Cache layout computations to reduce reflow
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);

      // Batch style updates
      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);
    });
  }
}

customElements.define('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.classes = {
      pinned: 'header-pinned',
    };
  }

  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;

    // Cache initial bounds for better performance
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.beforeInit();
  }

  beforeInit() {
    this.headerSection.classList.add('header-sticky');
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    window.addEventListener('scroll', this.onScrollHandler.bind(this), false);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
    const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add('header-scrolled');

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.classes.pinned);
        } else {
          // Sticky on scroll up.
          if (scrollTop < this.currentScrollTop || scrollTop < headerBoundsBottom + 100) {
            document.body.classList.add(this.classes.pinned);
          } else {
            document.body.classList.remove(this.classes.pinned);
          }
        }
      } else {
        headerSection.classList.remove('header-scrolled');
        document.body.classList.remove(this.classes.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }
}

customElements.define('sticky-header', StickyHeader, { extends: 'header' });

const clearDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();
    // Initialize properties
    this.classes = { bodyClass: 'has-dropdown-menu' };
    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    };

    // Reference to first and last child elements
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;

    // Initial state based on attributes
    this._open = this.hasAttribute('open');

    // Event listeners for summary element
    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) {
          this.open = true;
        }
      });
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) {
          this.open = false;
        }
      });
    }

    // Binding methods to ensure 'this' context is correct when they are called
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);

    // Setup hover detection with debouncing
    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener.bind(this));
    this.addEventListener('mouseleave', this.detectHoverListener.bind(this));
  }

  set open(value) {
    // Check if the new value is different from the current value
    if (value !== this._open) {
      // Update the internal state
      this._open = value;

      // Perform actions based on whether the element is connected to the DOM
      if (this.isConnected) {
        // If connected, perform a transition
        this.transition(value);
      } else {
        // If not connected, directly manipulate the 'open' attribute
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    // For touch devices, always use click events
    if (FoxTheme.config.isTouch) {
      return 'click';
    }

    // For non-touch devices, check for custom trigger attribute
    return this.getAttribute('trigger') || 'click';
  }

  get level() {
    if (this.hasAttribute('level')) {
      return this.getAttribute('level');
    } else {
      return 'top';
    }
  }

  handleSummaryClick(event) {
    // Prevent the default action of the event
    event.preventDefault();

    // Check if the device is not touch-enabled and the trigger type is 'hover'
    if (!FoxTheme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      // If conditions are met, navigate to the URL specified in 'data-link'
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      // Otherwise, toggle the 'open' state
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      // Increment the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) + 1);

      // Add class to body and set attributes
      document.body.classList.add(this.classes.bodyClass);
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      setTimeout(() => this.contentElement.setAttribute('open', ''), 100);

      // Add event listeners
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);

      // Perform the transition in animation
      await this.showWithTransition();

      // Check for reverse condition (not implemented here for brevity)
      this.needsReverse();

      // Wait for the after show event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
    } else {
      // Decrement the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) - 1);

      // Toggle class on body based on lock count
      document.body.classList.toggle(this.classes.bodyClass, clearDropdownCount.get(DetailsDropdown) > 0);

      // Remove attributes and event listeners
      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscKeyPress);
      document.removeEventListener('focusout', this.handleFocusOut);

      // Perform the transition out animation
      await this.hideWithTransition();

      // Conditionally remove the 'open' attribute
      if (!this.open) {
        this.removeAttribute('open');
      }

      // Wait for the after hide event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  async showWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: 'visible' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay: FoxTheme.config.motionReduced ? 0 : 0.1,
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, 'translateY(0)'] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async hideWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: 'hidden' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger === 'hover') {
      this.open = event.type === 'mouseenter';
    }
  }

  needsReverse() {
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
clearDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true;
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }

  async showWithTransition() {
    // Perform the animation on the first child of the content element
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'visible',
        transform: ['translateY(-100%)', 'translateY(0)'],
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }

  async hideWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'hidden',
        transform: 'translateY(-100%)',
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }
}

customElements.define('details-mega', DetailsMega, { extends: 'details' });

class MenuDrawerDetails extends HTMLDetailsElement {
  connectedCallback() {
    if (this._accordionReady) return;
    this._accordionReady = true;
    this.summary = this.querySelector(':scope > summary');
    this.panel = this.querySelector(':scope > .nr-menu__panel');
    if (!this.summary || !this.panel) return;

    this.summary.setAttribute('aria-expanded', String(this.open));
    this.summary.addEventListener('click', (event) => {
      event.preventDefault();
      this.setExpanded(!this._expanded);
    });
    this._expanded = this.open;
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this._expanded) return;
      event.preventDefault();
      event.stopPropagation();
      this.setExpanded(false);
      this.summary.focus();
    });
  }

  setExpanded(expanded, animate = true) {
    if (!this.panel) return;
    const startHeight = this.open ? this.panel.getBoundingClientRect().height : 0;
    if (this._animation) this._animation.cancel();
    this._animation = null;
    this._expanded = expanded;
    this.summary.setAttribute('aria-expanded', String(expanded));

    // Keep the details open until its closing animation has finished.
    this.open = true;
    const endHeight = expanded ? this.panel.scrollHeight : 0;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reduced || !this.panel.animate) {
      this.open = expanded;
      this.panel.style.removeProperty('overflow');
      return;
    }

    this.panel.style.overflow = 'hidden';
    const animation = this.panel.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: 180, easing: 'ease-out' }
    );
    this._animation = animation;
    animation.finished.then(() => {
      if (this._animation !== animation) return;
      this.open = expanded;
      this.panel.style.removeProperty('overflow');
      this._animation = null;
    }).catch(() => {});
  }

  onCloseButtonClick() {
    this.setExpanded(false, false);
  }
}
customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get menuItems() {
    if (!this._menuItems) {
      this._menuItems = this.querySelectorAll('.nr-menu > li');
    }
    return this._menuItems;
  }

  get requiresBodyAppended() {
    // Escape the sticky header's stacking context so the drawer covers it.
    return true;
  }

  prepareToShow() {
    super.prepareToShow();
    setTimeout(() => {
      this.animateMenuItems();
    }, 300);
  }

  animateMenuItems() {
    if (FoxTheme.config.motionReduced) return;

    FoxTheme.Motion.animate(
      this.menuItems,
      {
        transform: ['translateX(-20px)', 'translateX(0)'],
        opacity: [0, 1],
      },
      {
        duration: 0.6,
        easing: [0.075, 0.82, 0.165, 1],
        delay: FoxTheme.Motion.stagger(0.1),
      }
    ).finished.then(() => {
      this.menuItems.forEach((item) => item.removeAttribute('style'));
    });
  }

  handleAfterHide() {
    super.handleAfterHide();
    setTimeout(() => {
      this.querySelectorAll('details[is=menu-drawer-details]').forEach((menu) => {
        menu.onCloseButtonClick();
      });
    });
  }
}
customElements.define('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector('button');
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns);
  }

  initSlide() {
    const slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label',
    });
    slider.init();

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', function () {
        const slide = element.closest('.swiper-slide');

        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide));
      });
    });
  }
}
customElements.define('menu-product-list', MenuProductList);

class HeaderAccount extends HTMLElement {
  async connectedCallback() {
    const account = this.querySelector('shopify-account');

    if (!account) return;

    this.injectAnimation(account);
  }

  injectAnimation(account) {
    const shadow = account.shadowRoot;

    if (!shadow || shadow.querySelector('#custom-account-styles')) return;

    const style = document.createElement('style');
    style.id = 'custom-account-styles';

    style.textContent = `
      @keyframes dialogScaleIn {
        0% {
          transform: scale(0.8);
          opacity: 0;
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }
      @keyframes dialogScaleOut {
        0% {
          transform: scale(1);
          opacity: 1;
        }

        100% {
          transform: scale(0.8);
          opacity: 0;
        }
      }
      @keyframes dialogBackdropIn {
        0% {
          opacity: 0;
        }

        100% {
          opacity: 1;
        }
      }
      @keyframes dialogBackdropOut {
        0% {
          opacity: 1;
        }

        100% {
          opacity: 0;
        }
      }
      @media (min-width: 751px) {
        dialog .account {
          height: fit-content;
          background: var(--shopify-account-color-background);
          border-radius: inherit;
        }
        dialog[open] {
          animation: dialogScaleIn 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing {
          animation: dialogScaleOut 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog[open]::backdrop {
          cursor: zoom-out;
          animation: dialogBackdropIn 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing::backdrop {
          animation: dialogBackdropOut 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        } 
      }
      @media (max-width: 750px) {
        dialog .account__content {
          padding-bottom: 2.4rem;
        }
      }
    `;

    shadow.prepend(style);
  }
}

customElements.define('header-account', HeaderAccount);
